// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import useProjectLabels from 'hooks/useProjectLabels';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import styled from 'styled-components';
import CanvasContainer from './canvas/CanvasContainer';
import IconPredict from 'components/treactui-template/icons/IconPredict';
import { workingCopy } from 'model/ProjectMetaMessageExtensions';
import { useAppSelector } from 'store/hooks';
import { resetMask, setMask } from './canvas/DrawHandlerExtension';
import ParamsWithId from 'model/ParamsWithId';
import useAppPermissions from 'hooks/useAppPermissions';
import DatasetTag from 'components/common/DatasetTag';
import { DatasetFilterType, QCResult } from '../data/filter/filters-defaults';
import { LabelMetaDataContext } from './LabelMetaDataProvider';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { Button, Colors, DropdownContent } from '@tls/treact-ui';
import { IconDelete, IconLoading } from '@tls/treact-ui/icons';
import useGlobalKeyboardEvent from 'hooks/useGlobalKeyboardEvent';

const dataSetMappingOptions = [
  {
    id: 0,
    label: 'Training set',
    icon: <DatasetTag dataset={DatasetFilterType.TrainingSet} size={'24px'} />,
  },
  {
    id: 1,
    label: 'Evaluation set',
    icon: <DatasetTag dataset={DatasetFilterType.EvaluationSet} size={'24px'} />,
  },
];

type DataSetMappingOption = (typeof dataSetMappingOptions)[number];

export default function LabelImage() {
  const { t } = useTranslation();
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);

  const { selectedMd5, name, labels } = useProjectLabels();
  const { hasPermissionForProject } = useAppPermissions();
  const [loading, setLoading] = useState<'reset' | 'predict'>();
  const { setIsValidationDataset, isValidationDataset, setQcResult } = useContext(LabelMetaDataContext);
  useEffect(() => {
    const selectedLabel = labels.find(l => l.imageMd5 === selectedMd5);
    setIsValidationDataset(selectedLabel?.isValidation ?? false);
    setQcResult(selectedLabel?.qcResult ?? QCResult.Undefined);
  }, [id, selectedMd5, labels, setIsValidationDataset, setQcResult]);

  const predict = useCallback(async () => {
    if (!!loading || !selectedMd5) return;
    const imageId = selectedMd5;
    setLoading('predict');
    await setMask(`projects/${id}/predict/${workingCopy}/${imageId}`, { imageId });
    setLoading(undefined);
  }, [id, loading, selectedMd5]);

  const reset = useCallback(() => {
    setLoading('reset');
    resetMask();
    setLoading(undefined);
  }, []);
  const isQC = useMemo(
    () => selectedProject?.projectUseCase?.useCase === UseCase.QUALITY_CHECK,
    [selectedProject?.projectUseCase?.useCase]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'p') {
        predict();
      }
    },
    [predict]
  );

  useGlobalKeyboardEvent('keydown', handleKeyPress);

  const predictEnabled = useAppSelector(s => s.label.predictEnabled);

  const hasMask = labels.find(l => l.imageMd5 === selectedMd5)?.hasMask;

  return (
    <Container>
      <Header>
        <span className='font-size_m font-weight_bold'>{t('project.label.image.title')}</span>
        <Title>
          <p className='font-size_m ellipsis' data-testid='label-image-editor-image-name'>
            {name}
          </p>
        </Title>
        {isQC && (
          <>
            <DropdownContent<DataSetMappingOption>
              iconSelector={o => o.icon}
              style={{ width: '12rem' }}
              onChange={o => {
                setIsValidationDataset(o.id === 1);
              }}
              selectedOption={isValidationDataset ? dataSetMappingOptions[1] : dataSetMappingOptions[0]}
              label={t('project.label.image.datasetMapping')}
              options={dataSetMappingOptions}
              keySelector={o => o.id}
              labelSelector={o => o.label}
              data-testid='label-image-dataset-dropdown'
              dataTestIdSelector={option => `label-image-dataset-option-${option.id}`}
            />
            <Divider />
          </>
        )}

        <Button
          icon={loading === 'predict' ? <IconLoading /> : <IconPredict width='1.75rem' />}
          label={t('project.label.image.predict')}
          variant='secondary'
          onClick={predict}
          disabled={!predictEnabled || !!loading || !selectedMd5 || !hasPermissionForProject}
          data-testid='label-image-predict-button'
        />
        <Button
          icon={loading === 'reset' ? <IconLoading /> : <IconDelete />}
          label={t('project.label.image.deleteLabel')}
          variant='secondary'
          onClick={reset}
          disabled={!!loading || !selectedMd5}
          data-testid='label-image-reset-label-button'
        />
      </Header>
      <CanvasContainer imageId={selectedMd5} projectId={id} hasMask={hasMask} />
    </Container>
  );
}
const Divider = styled.div`
  height: 3rem;
  margin-bottom: 0.5rem;
  border-right: 1px solid ${Colors.trgrey2.hex};
`;
const Container = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const Title = styled.div`
  flex: 1;
`;

const Header = styled.div`
  justify-content: end;
  flex-wrap: wrap;
  padding: 0.5rem 0.5rem 0;
  display: flex;
  column-gap: 1rem;
  align-items: center;
  .ellipsis {
    text-overflow: ellipsis;
    word-break: normal;
    white-space: nowrap;
    overflow: hidden;
  }
  p {
    margin: 0;
  }
`;
